// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import { generateClient } from "@aws-amplify/api";
import { fetchAuthSession } from "@aws-amplify/auth";

import { costRateUpdate } from "../graphql/usageCostAllocation/mutations";
import { costRateGetCurrent } from "../graphql/usageCostAllocation/queries";
import { CostRateConfig } from "../types/usageCostAllocation";

/**
 * Validate that a cost rate value is a non-negative number.
 * Rejects negative numbers, NaN, Infinity, and non-numeric types.
 */
export function validateCostRate(value: unknown): boolean {
	if (typeof value !== "number") return false;
	if (!Number.isFinite(value)) return false;
	return value >= 0;
}

/**
 * Query AppSync for the latest CostRateConfig.
 */
export async function getCurrentCostRates(): Promise<CostRateConfig> {
	const client = generateClient({ authMode: "userPool" });

	const response = await client.graphql({
		query: costRateGetCurrent,
	});

	const data = (response as { data: { costRateGetCurrent: CostRateConfig } })
		.data.costRateGetCurrent;

	return data;
}

/**
 * Validate input and persist updated cost rates via GraphQL mutation.
 * Sets effectiveTimestamp to current unix timestamp and updatedBy from Cognito session.
 */
export async function updateCostRates(
	rates: Partial<CostRateConfig>
): Promise<CostRateConfig> {
	if (
		rates.characterCostRate !== undefined &&
		!validateCostRate(rates.characterCostRate)
	) {
		throw new Error(
			"Invalid characterCostRate: must be a non-negative number"
		);
	}

	const client = generateClient({ authMode: "userPool" });

	const authSession = await fetchAuthSession();
	const updatedBy =
		(authSession.tokens?.idToken?.payload?.sub as string) ??
		authSession.identityId ??
		"";

	const effectiveTimestamp = Math.floor(Date.now() / 1000);

	const input = {
		id: "default",
		characterCostRate: rates.characterCostRate,
		effectiveTimestamp,
		updatedBy,
	};

	const response = await client.graphql({
		query: costRateUpdate,
		variables: { input },
	});

	const data = (response as { data: { costRateUpdate: CostRateConfig } })
		.data.costRateUpdate;

	return data;
}
